import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'
import { useSelector } from 'react-redux'

import { db } from '../firebase'
// import { useIntercom } from "react-use-intercom";

const useSection = ({ id, enabled }) => {
  const {
    data: course,
    status: statusCourse,
    error: errorCourse,
    isPending,
  } = useQuery({
    queryKey: ['course', id],
    queryFn: () => getCourse(id),
    enabled: enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: 300000,
  })

  return {
    course,
    statusCourse,
    errorCourse,
    isLoading: isPending,
  }
}

export default useSection

export const getCourse = async courseId => {
  try {
    const ref = doc(db, 'courses', courseId)
    const docSnap = await getDoc(ref)

    if (!docSnap.exists()) {
      throw new Error('Course does not exist')
    }

    return { ...docSnap.data(), id: docSnap.id }
  } catch (error) {
    console.log(error)
    throw new Error(error)
  }
}

export const useUpdateSection = id => {
  const queryClient = useQueryClient()
  const { mutateAsync, isError, isPending } = useMutation({
    mutationKey: ['set', id],
    mutationFn: data => updateCourse(id, data),
    onSuccess: newSet => {
      queryClient.invalidateQueries({
        queryKey: ['sections', 'owners'],
      })
    },
    onError: error => {
      console.log(error)
    },
  })

  return {
    updateSection: mutateAsync,
    isError,
    isLoading: isPending,
  }
}

export const updateCourse = async (id, data) => {
  try {
    const ref = doc(db, 'courses', id)

    await updateDoc(ref, data)

    return data
  } catch (error) {
    console.log(error)
    throw new Error(error)
  }
}

export const useCreateSection = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isError, isPending, data } = useMutation({
    mutationKey: ['set'],
    mutationFn: data => createCourse(data),
    onSuccess: newSet => {
      queryClient.setQueryData(['course', newSet.id], newSet)

      return newSet
    },
    onError: error => {
      console.log(error)
    },
  })

  return {
    createCourse: mutateAsync,
    errorCreateCourse: isError,
    loadingCreateCourse: isPending,
    data,
  }
}

export const createCourse = async data => {
  try {
    const ref = collection(db, 'courses')
    const newSet = await addDoc(ref, data)

    return { ...data, id: newSet.id }
  } catch (error) {
    console.log(error)
    throw new Error(error)
  }
}

export const useSections = (type = 'owners', withAssignmentTotals = false, enabled = true) => {
  const { uid } = useSelector(({ user: { auth } }) => auth)
  const { data, isPending, isError, isSuccess } = useQuery({
    queryKey: ['sections', type],
    queryFn: () => getSections(uid, type, withAssignmentTotals),
    enabled: !!uid && enabled,
  })

  return {
    sections: data,
    isLoading: isPending,
    isSuccess,
    isError,
  }
}

export const getSections = async (uid, type, withAssignmentTotals) => {
  try {
    const c = collection(db, 'courses')
    const q = query(c, where(type, 'array-contains', uid))
    const sectionsDocs = await getDocs(q)
    let sections = sectionsDocs.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }))
    // if (type === "owners") {
    // 	window._cio.identify({
    // 		id: uid,
    // 		sections: sections?.length || 0,
    // 	});
    // }

    if (withAssignmentTotals) {
      // get count of assignments with courseId == section.id
      sections = await Promise.all(
        sections.map(async section => {
          const ref = collection(db, 'assignments')
          const q = query(ref, where('courseId', '==', section.id))
          const snap = await getCountFromServer(q)
          const totol = snap.data().count

          return { ...section, totalAssignments: totol }
        }),
      )
    }

    return sections
  } catch (error) {
    throw new Error(error)
  }
}

export const useDeleteSection = id => {
  const queryClient = useQueryClient()
  const { mutateAsync, isError, isPending } = useMutation({
    mutationKey: ['delete', id],
    mutationFn: () => deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sections', 'owners'],
      })
    },
    onError: error => {
      console.log(error)
    },
  })

  return {
    deleteSection: mutateAsync,
    isError,
    isLoading: isPending,
  }
}

export const deleteCourse = async id => {
  console.log('deleteCourse', id)
  try {
    const ref = doc(db, 'courses', id)

    await deleteDoc(ref)

    return true
  } catch (error) {
    console.log(error)
    throw new Error(error)
  }
}
